import type { Severity } from '@studio/design-system/messaging';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { DefaultMessage } from './constants.js';
import type { Options } from './types.js';

/** A custom `Error` class for application errors. */
export class StudioError extends Error {
  /** Error ID. */
  public readonly id: T_Maybe<number | string>;

  /** Severity level. */
  public readonly severity: Severity;

  /** Error title. */
  public readonly title: string;

  /**
   * `<StudioError>` constructor.
   *
   * @param message - Error message.
   * @param options - Error options.
   */
  public constructor(message?: T_Maybe<string>, options: Options = {}) {
    const { cause, id, severity, title } = options;

    super(message ?? DefaultMessage, cause ? { cause } : undefined);

    this.id = id;
    this.severity = severity ?? 'error';
    this.name = 'StudioError';
    this.title = title ?? 'Error';
  }
}

/* eslint-disable sort-exports/sort-exports */

/** A custom `Error` class for 404 Not Found-type errors. */
export class NotFoundError extends StudioError {
  /**
   * `<NotFoundError>` constructor.
   *
   * @param message - Error message.
   * @param options - Error options.
   */
  public constructor(message?: T_Maybe<string>, options?: T_Maybe<Options>) {
    super(message ?? 'Page not found.', options);

    this.name = 'NotFoundError';
  }
}

/** A custom `Error` class for offline/network connectivity errors. */
export class OfflineError extends StudioError {
  /**
   * `<OfflineError>` constructor.
   *
   * @param options - Error options.
   */
  public constructor(options?: T_Maybe<Options>) {
    super(
      "It looks like you're not connected to the internet. Please reconnect to continue logging in.",
      {
        ...options,
        title: "You're Offline"
      }
    );

    this.name = 'OfflineError';
  }
}
